import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { canonical } from "@/lib/seo";

const TITLE = "Terms of Service | Durafoam Roofing";
const DESCRIPTION = "Terms of service for the Durafoam Roofing website.";

export const Route = createFileRoute("/terms")({
  head: () => {
    const url = canonical("/terms");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "noindex" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: TermsPage,
});

function TermsPage() {
  return (
    <Container className="prose prose-slate max-w-3xl py-12 lg:py-16">
      <h1 className="text-3xl font-bold text-primary">Terms of Service</h1>
      <p className="text-sm text-[var(--muted-foreground)]">Last updated: June 2026</p>

      <p>
        These Terms of Service govern your use of the Durafoam Roofing website
        (durafoaminc.com) operated by Durafoam Roofing LLC, 2810 E Mohawk Ln, Phoenix AZ 85050.
        By using this site you agree to these terms.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Website use and content ownership</h2>
      <p>
        All content on this website — text, images, photos, logos, schema, layout, and design — is
        owned by Durafoam Roofing LLC. You may view, share, and link to our pages for personal,
        non-commercial use. You may not copy, republish, or use our content for commercial
        purposes without our written permission.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">No warranty on website information</h2>
      <p>
        We work hard to keep this site accurate, but information here is provided "as is" without
        warranty of any kind. Roofing pricing, code requirements, and best practices change.
        Information on this site is for general guidance only and is not a substitute for an
        on-site inspection.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Estimates and quotes</h2>
      <p>
        Any pricing ranges, cost calculators, or estimates shown on this website are for
        informational purposes only. Final pricing for any roofing work requires an on-site
        inspection and a written estimate signed by both parties.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Durafoam Roofing LLC is not liable for any
        indirect, incidental, or consequential damages arising from your use of this website or
        reliance on information shown here.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Privacy</h2>
      <p>
        See our <a href="/privacy-policy" className="text-accent hover:underline">Privacy Policy</a> for
        information on what data we collect and how we use it.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Governing law</h2>
      <p>
        These terms are governed by the laws of the State of Arizona. Any dispute will be resolved
        in the state or federal courts located in Maricopa County, Arizona.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Contact</h2>
      <p>
        Questions about these terms? Email{" "}
        <a href="mailto:quality@durafoaminc.com" className="text-accent hover:underline">
          quality@durafoaminc.com
        </a>
        .
      </p>
    </Container>
  );
}
